/** @jsx jsx */
import { Flex, Status, TabButton } from "@eworkit/iris";
import { ConversationStatus, UnreadMessagesConversationIndicator } from "@eworkit/messenger";
import React, { FC, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { RoutedTabs } from "router";
import { jsx } from "theme-ui";

import { useFeatureFlags } from "@/components/FeatureFlags";
import { UserContext } from "@/components/User";
import { FEATURE_FLAG } from "@/data/feature-flags";
import { JobApplication } from "@/data/job-applications/types/JobApplication";
import { useApplicationConversation } from "@/views-new/chat/useConversation";
import { router } from "@/views/router";

export interface JobApplicationDetailsTabsProps {
  jobApplication: JobApplication;
}

export const JobApplicationDetailsTabs: FC<JobApplicationDetailsTabsProps> = React.memo(({ jobApplication }) => {
  const { t } = useTranslation("session");
  const { contextCompany, hasCompanyManagerPrivileges } = useContext(UserContext);

  const { isFeatureEnabled } = useFeatureFlags();
  const { conversationId } = useApplicationConversation(jobApplication.id);

  const details = useCallback(
    (active: boolean, index: number) => (
      <TabButton
        key={index}
        active={active}
        as={Link}
        to={router.get(`job-applications.details.details`, { jobApplicationId: jobApplication.id }).path}>
        {t("JOB_APPLICATIONS.DETAIL.TABS.DETAILS")}
      </TabButton>
    ),
    [t, jobApplication],
  );

  const lifecycle = useCallback(
    (active: boolean, index: number) => (
      <TabButton
        key={index}
        active={active}
        as={Link}
        to={router.get(`job-applications.details.lifecycle`, { jobApplicationId: jobApplication.id }).path}>
        {t("JOB_APPLICATIONS.DETAIL.TABS.LIFECYCLE")}
      </TabButton>
    ),
    [t, jobApplication],
  );

  const related = useCallback(
    (active: boolean, index: number) => (
      <TabButton
        key={index}
        active={active}
        as={Link}
        to={router.get(`job-applications.details.related`, { jobApplicationId: jobApplication.id }).path}
        placeholderRight={<Status size="sm">{jobApplication.relatedEntities?.totalCount}</Status>}>
        {t("JOB_APPLICATIONS.DETAIL.TABS.RELATED")}
      </TabButton>
    ),
    [t, jobApplication],
  );

  const messages = useCallback(
    (active: boolean, index: number) => (
      <TabButton
        key={index}
        active={active}
        as={Link}
        to={router.get(`job-applications.details.messages`, { jobApplicationId: jobApplication.id }).path}
        placeholderLeft={conversationId && <UnreadMessagesConversationIndicator conversationId={conversationId} />}
        placeholderRight={conversationId && <ConversationStatus size="sm" id={conversationId} />}>
        {t("JOB_APPLICATIONS.DETAIL.TABS.MESSAGES")}
      </TabButton>
    ),
    [t, jobApplication, conversationId],
  );

  const tabs = useMemo(
    () => ({
      details,
      lifecycle,
      related,
      messages:
        isFeatureEnabled(FEATURE_FLAG.MESSAGE_CENTER) &&
        conversationId &&
        (!contextCompany || hasCompanyManagerPrivileges())
          ? messages
          : undefined,
    }),
    [
      details,
      lifecycle,
      related,
      messages,
      conversationId,
      isFeatureEnabled,
      contextCompany,
      hasCompanyManagerPrivileges,
    ],
  );

  return (
    <Flex flexDirection="row" alignItems="flex-end" sx={{ overflowX: "auto" }}>
      <RoutedTabs routeParam="tab" views={tabs} />
    </Flex>
  );
});
